/**
 * Global player context for the admin SPA (TASK-054, design §4.6 / §5.10).
 *
 * Holds the currently-playing track (or `null`), exposes commands to start a
 * track, toggle play/pause, seek, close. The actual audio element + hls.js
 * instance live in {@link PlayerBar}; this provider only carries the state
 * needed by the bar AND by call-sites (Listen buttons) to start playback.
 *
 * Two playback modes share the same `currentTrack`:
 *   - single  — `setTrack(track)` plays one track and clears any queue.
 *   - queue   — `playQueue(tracks, startIndex)` plays through an ordered list;
 *               `<PlayerBar>` auto-advances on track end and surfaces
 *               prev/next controls. Used by the preset "play the resolved
 *               track list" preview (and reusable for any "play all" list).
 *
 * State is UI-only and intentionally NOT persisted to browser storage —
 * playback state should not survive a page reload (the BE audit log treats
 * each Listen as its own event).
 */

import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface PlayerTrack {
  /** Track id — passed straight into `/admin/tracks/:id/playback.m3u8`. */
  id: string;
  title: string;
  artist: string | null;
  /** Pre-resolved cover URL when available; null falls through to placeholder. */
  coverUrl: string | null;
  /** Optional display extras for the bar subtitle. */
  bpm?: number | null;
  camelotKey?: string | null;
  durationSeconds?: number | null;
}

export interface PlayerContextValue {
  currentTrack: PlayerTrack | null;
  isPlaying: boolean;
  /** Play a single track. Clears any active queue. */
  setTrack: (track: PlayerTrack) => void;
  /** Play through an ordered list starting at `startIndex` (default 0).
   *  `opts.fadeSeconds` > 0 makes `<PlayerBar>` ramp the volume over the
   *  first/last N seconds of every queued track (preset crossfade).
   *  `opts.loadMore` turns the queue endless: entering the last queued track
   *  prefetches a continuation batch (given the tail track + every queued id)
   *  and appends it. Return `[]` to end the queue. */
  playQueue: (
    tracks: PlayerTrack[],
    startIndex?: number,
    opts?: {
      fadeSeconds?: number;
      loadMore?: (
        lastTrack: PlayerTrack,
        queuedIds: string[],
      ) => Promise<PlayerTrack[]>;
    },
  ) => void;
  /** Crossfade window for the active queue; 0 = no fade (single-track mode). */
  fadeSeconds: number;
  /** Advance to the next queued track. No-op outside a queue / at the end. */
  playNext: () => void;
  /** Step back to the previous queued track. No-op outside a queue / at the start. */
  playPrev: () => void;
  /** Whether a queue is active and a next/previous track exists. */
  hasNext: boolean;
  hasPrev: boolean;
  togglePlay: () => void;
  /** Internal — used by `<PlayerBar>` to keep the context in sync with the
   *  underlying `<audio>` element's play/pause events. */
  setIsPlaying: (playing: boolean) => void;
  close: () => void;
}

export const PlayerContext = createContext<PlayerContextValue | null>(null);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const [currentTrack, setCurrentTrack] = useState<PlayerTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // Queue mode: `queue` is the ordered list, `queueIndex` points at the
  // currently-playing entry (-1 when playing a single track outside a queue).
  const [queue, setQueue] = useState<PlayerTrack[]>([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const [fadeSeconds, setFadeSeconds] = useState(0);
  // Endless-queue continuation. The callback lives in a ref (it must survive
  // the starting component unmounting — the bar is global); `canLoadMore`
  // flips off once a batch comes back empty. `loadingMoreRef` dedupes the
  // prefetch against a concurrent user-clicked next.
  const loadMoreRef = useRef<
    | ((lastTrack: PlayerTrack, queuedIds: string[]) => Promise<PlayerTrack[]>)
    | null
  >(null);
  const loadingMoreRef = useRef(false);
  const [canLoadMore, setCanLoadMore] = useState(false);

  const setTrack = useCallback((track: PlayerTrack) => {
    setQueue([]);
    setQueueIndex(-1);
    setFadeSeconds(0);
    loadMoreRef.current = null;
    setCanLoadMore(false);
    setCurrentTrack(track);
    setIsPlaying(true);
  }, []);

  const playQueue = useCallback(
    (
      tracks: PlayerTrack[],
      startIndex = 0,
      opts: {
        fadeSeconds?: number;
        loadMore?: (
          lastTrack: PlayerTrack,
          queuedIds: string[],
        ) => Promise<PlayerTrack[]>;
      } = {},
    ) => {
      if (tracks.length === 0) return;
      const idx = Math.min(Math.max(startIndex, 0), tracks.length - 1);
      setQueue(tracks);
      setQueueIndex(idx);
      setFadeSeconds(Math.max(0, opts.fadeSeconds ?? 0));
      loadMoreRef.current = opts.loadMore ?? null;
      setCanLoadMore(opts.loadMore !== undefined);
      setCurrentTrack(tracks[idx]);
      setIsPlaying(true);
    },
    [],
  );

  // Prefetch the continuation as soon as the LAST queued track starts —
  // by the time its tail fades out the next batch is already appended, so
  // auto-advance never hits a loading gap. A failed/empty batch closes the
  // queue (`canLoadMore=false`) and playback simply stops at the end.
  useEffect(() => {
    const loadMore = loadMoreRef.current;
    if (!loadMore || !canLoadMore) return;
    if (queueIndex < 0 || queueIndex !== queue.length - 1) return;
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    void loadMore(queue[queueIndex], queue.map((t) => t.id))
      .then((more) => {
        if (more.length === 0) {
          setCanLoadMore(false);
          return;
        }
        // Functional append; a queue closed mid-flight stays closed.
        setQueue((q) => (q.length === 0 ? q : [...q, ...more]));
      })
      .catch(() => {
        setCanLoadMore(false);
      })
      .finally(() => {
        loadingMoreRef.current = false;
      });
  }, [canLoadMore, queue, queueIndex]);

  const playNext = useCallback(() => {
    if (queueIndex < 0 || queueIndex + 1 >= queue.length) return;
    const next = queueIndex + 1;
    setQueueIndex(next);
    setCurrentTrack(queue[next]);
    setIsPlaying(true);
  }, [queue, queueIndex]);

  const playPrev = useCallback(() => {
    if (queueIndex <= 0) return;
    const prev = queueIndex - 1;
    setQueueIndex(prev);
    setCurrentTrack(queue[prev]);
    setIsPlaying(true);
  }, [queue, queueIndex]);

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const close = useCallback(() => {
    setCurrentTrack(null);
    setIsPlaying(false);
    setQueue([]);
    setQueueIndex(-1);
    setFadeSeconds(0);
    loadMoreRef.current = null;
    setCanLoadMore(false);
  }, []);

  const hasNext = queueIndex >= 0 && queueIndex + 1 < queue.length;
  const hasPrev = queueIndex > 0;

  return (
    <PlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        setTrack,
        playQueue,
        fadeSeconds,
        playNext,
        playPrev,
        hasNext,
        hasPrev,
        togglePlay,
        setIsPlaying,
        close,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}
